import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Fade,
  Tooltip,
  Badge,
} from "@mui/material";
import {
  Delete,
  Edit,
  Add,
  Category as CategoryIcon,
  Search,
  FolderOpen,
  Label,
} from "@mui/icons-material";
import Header from "../components/Header";
import categoryApi from "../api/categoryApi";
import Footer from "../components/Footer";

const Category = () => {
  const [categories, setCategories] = useState({
    categories: [],
    subCategories: [],
  });
  const [categoryName, setCategoryName] = useState("");
  const [subcategoryName, setSubcategoryName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await categoryApi.getAllCategories();
      if (response.success) {
        setCategories(response.data.data.data);
      } else {
        setError(response.error || "Failed to fetch categories");
      }
    } catch (error) {
      setError("Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAddCategory = async () => {
    if (!categoryName.trim()) return;

    setLoading(true);
    try {
      if (editMode) {
        const response = await categoryApi.editCategory(editId, {
          name: categoryName,
        });
        if (response.success) {
          await fetchCategories();
          showSnackbar("Category updated successfully");
          resetCategoryForm();
        } else {
          showSnackbar(response.error || "Failed to update category", "error");
        }
      } else {
        const response = await categoryApi.addCategory({
          name: categoryName,
        });
        if (response.success) {
          await fetchCategories();
          showSnackbar("Category added successfully");
          resetCategoryForm();
        } else {
          showSnackbar(response.error || "Failed to add category", "error");
        }
      }
    } catch (error) {
      showSnackbar("An error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetCategoryForm = () => {
    setCategoryName("");
    setEditMode(false);
    setEditId(null);
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      setLoading(true);
      try {
        const response = await categoryApi.deleteCategory(id);
        if (response.success) {
          await fetchCategories();
          showSnackbar("Category deleted successfully");
        } else {
          showSnackbar(response.error || "Failed to delete category", "error");
        }
      } catch (error) {
        showSnackbar("An error occurred", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditCategory = (category) => {
    setCategoryName(category.name);
    setEditMode(true);
    setEditId(category.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAddSubcategory = async () => {
    if (!subcategoryName.trim() || !selectedCategory) return;

    setLoading(true);
    try {
      const response = await categoryApi.addSubCategory({
        name: subcategoryName,
        category_id: selectedCategory.id,
      });
      if (response.success) {
        await fetchCategories();
        showSnackbar("Subcategory added successfully");
        setSubcategoryName("");
        setOpenDialog(false);
        setSelectedCategory(null);
      } else {
        showSnackbar(response.error || "Failed to add subcategory", "error");
      }
    } catch (error) {
      showSnackbar("An error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubcategory = async (subcategoryId) => {
    if (window.confirm("Are you sure you want to delete this subcategory?")) {
      setLoading(true);
      try {
        const response = await categoryApi.deleteSubCategory(subcategoryId);
        if (response.success) {
          await fetchCategories();
          showSnackbar("Subcategory deleted successfully");
        } else {
          showSnackbar(
            response.error || "Failed to delete subcategory",
            "error"
          );
        }
      } catch (error) {
        showSnackbar("An error occurred", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  const openSubcategoryDialog = (category) => {
    setSelectedCategory(category);
    setSubcategoryName("");
    setOpenDialog(true);
  };

  // Filter categories based on search
  const filteredCategories = categories?.categories?.filter((cat) =>
    cat.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getSubcategoryCount = (categoryId) => {
    return (
      categories.subCategories?.filter((sub) => sub.parent_id === categoryId)
        .length || 0
    );
  };

  if (loading && categories.categories?.length === 0) {
    return (
      <>
        <Header title="Category Management" />
        <Container
          maxWidth="lg"
          sx={{ mt: 4, display: "flex", justifyContent: "center" }}
        >
          <CircularProgress />
        </Container>
      </>
    );
  }

  return (
    <>
      <Header title="Category Management" />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: 2,
                background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a8f 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FolderOpen sx={{ color: "white", fontSize: "1.8rem" }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#1e3a5f",
                }}
              >
                Category Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Organize and manage your research categories and subcategories
              </Typography>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setError("")}
          >
            {error}
          </Alert>
        )}

        {/* Add/Edit Category Section */}
        <Card
          elevation={0}
          sx={{
            mb: 4,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            background: editMode
              ? "linear-gradient(135deg, #fff8e1 0%, #fff9c4 100%)"
              : "white",
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 3,
              }}
            >
              <CategoryIcon
                sx={{
                  color: editMode ? "#f57c00" : "#1e3a5f",
                  fontSize: "1.8rem",
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: editMode ? "#f57c00" : "#1e3a5f",
                }}
              >
                {editMode ? "Edit Category" : "Create New Category"}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <TextField
                fullWidth
                label="Category Name"
                placeholder="Enter category name"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Label sx={{ color: "#1e3a5f" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#1e3a5f",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1e3a5f",
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleAddCategory}
                disabled={loading || !categoryName.trim()}
                sx={{
                  minWidth: { xs: "100%", sm: "140px" },
                  py: 1.5,
                  bgcolor: editMode ? "#f57c00" : "#1e3a5f",
                  "&:hover": {
                    bgcolor: editMode ? "#e65100" : "#152d4a",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : editMode ? (
                  "Update"
                ) : (
                  "Add Category"
                )}
              </Button>
              {editMode && (
                <Button
                  variant="outlined"
                  onClick={resetCategoryForm}
                  disabled={loading}
                  sx={{
                    minWidth: { xs: "100%", sm: "120px" },
                    py: 1.5,
                    borderColor: "#f57c00",
                    color: "#f57c00",
                    "&:hover": {
                      borderColor: "#e65100",
                      bgcolor: "rgba(245, 124, 0, 0.04)",
                    },
                  }}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Search Bar */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "#1e3a5f" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: 400,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "white",
              },
            }}
          />
        </Box>

        {/* Categories Grid */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#1e3a5f",
              mb: 3,
            }}
          >
            All Categories ({filteredCategories?.length || 0})
          </Typography>

          <Grid container spacing={3}>
            {filteredCategories?.map((cat, index) => {
              const subcategoryCount = getSubcategoryCount(cat.id);
              const categorySubcategories = categories.subCategories?.filter(
                (sub) => sub.parent_id === cat.id
              );

              return (
                <Grid item xs={12} sm={6} md={4} key={cat.id}>
                  <Fade in={true} timeout={300 + index * 100}>
                    <Card
                      elevation={0}
                      sx={{
                        height: "100%",
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor: "divider",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                          borderColor: "#1e3a5f",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        {/* Category Header */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            mb: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              flex: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 45,
                                height: 45,
                                borderRadius: 2,
                                bgcolor: "rgba(30, 58, 95, 0.1)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <CategoryIcon
                                sx={{ color: "#1e3a5f", fontSize: "1.4rem" }}
                              />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  color: "#1e3a5f",
                                  fontSize: "1.1rem",
                                  mb: 0.5,
                                }}
                              >
                                {cat.name}
                              </Typography>
                              <Badge
                                badgeContent={subcategoryCount}
                                color="primary"
                                sx={{
                                  "& .MuiBadge-badge": {
                                    bgcolor: "#1e3a5f",
                                  },
                                }}
                              >
                                <Chip
                                  label="Subcategories"
                                  size="small"
                                  sx={{
                                    bgcolor: "rgba(30, 58, 95, 0.08)",
                                    color: "#1e3a5f",
                                    fontSize: "0.75rem",
                                    height: 24,
                                  }}
                                />
                              </Badge>
                            </Box>
                          </Box>
                        </Box>

                        {/* Subcategories */}
                        <Box sx={{ mb: 2, minHeight: 60 }}>
                          {categorySubcategories?.length > 0 ? (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                              }}
                            >
                              {categorySubcategories.map((sub) => (
                                <Chip
                                  key={sub.id}
                                  label={sub.name}
                                  size="small"
                                  onDelete={() =>
                                    handleDeleteSubcategory(sub.id)
                                  }
                                  disabled={loading}
                                  sx={{
                                    bgcolor: "white",
                                    border: "1px solid #e0e0e0",
                                    "&:hover": {
                                      bgcolor: "rgba(30, 58, 95, 0.04)",
                                      borderColor: "#1e3a5f",
                                    },
                                  }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontStyle: "italic" }}
                            >
                              No subcategories yet
                            </Typography>
                          )}
                        </Box>

                        {/* Action Buttons */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            pt: 2,
                            borderTop: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <Button
                            size="small"
                            startIcon={<Add />}
                            onClick={() => openSubcategoryDialog(cat)}
                            disabled={loading}
                            sx={{
                              color: "#1e3a5f",
                              fontWeight: 500,
                              "&:hover": {
                                bgcolor: "rgba(30, 58, 95, 0.08)",
                              },
                            }}
                          >
                            Add Sub
                          </Button>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Tooltip title="Edit Category">
                              <IconButton
                                size="small"
                                onClick={() => handleEditCategory(cat)}
                                disabled={loading}
                                sx={{
                                  color: "#1e3a5f",
                                  "&:hover": {
                                    bgcolor: "rgba(30, 58, 95, 0.1)",
                                  },
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Category">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteCategory(cat.id)}
                                disabled={loading}
                                sx={{
                                  color: "#d32f2f",
                                  "&:hover": {
                                    bgcolor: "rgba(211, 47, 47, 0.08)",
                                  },
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              );
            })}
          </Grid>

          {filteredCategories?.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
              }}
            >
              <FolderOpen
                sx={{
                  fontSize: "4rem",
                  color: "#bdbdbd",
                  mb: 2,
                }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No categories found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchQuery
                  ? "Try adjusting your search query"
                  : "Create your first category to get started"}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Add Subcategory Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "rgba(30, 58, 95, 0.05)",
              fontWeight: 600,
              color: "#1e3a5f",
            }}
          >
            Add Subcategory to "{selectedCategory?.name}"
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              autoFocus
              fullWidth
              label="Subcategory Name"
              placeholder="Enter subcategory name"
              variant="outlined"
              value={subcategoryName}
              onChange={(e) => setSubcategoryName(e.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Label sx={{ color: "#1e3a5f" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mt: 1,
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: "#1e3a5f",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1e3a5f",
                  },
                },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setOpenDialog(false)}
              disabled={loading}
              sx={{
                color: "#555",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddSubcategory}
              variant="contained"
              disabled={loading || !subcategoryName.trim()}
              sx={{
                bgcolor: "#1e3a5f",
                "&:hover": {
                  bgcolor: "#152d4a",
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : "Add Subcategory"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{
              width: "100%",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              borderRadius: 2,
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
      <Footer />
    </>
  );
};

export default Category;
